import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { apiClient, storeSessionToken, useAuth } from "@/App";
import { Card, CardContent } from "@/components/ui/card";
import { Loader2, CheckCircle2, XCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";

export const MagicLinkVerify = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser, checkAuth } = useAuth();
  const [status, setStatus] = useState("verifying"); // verifying, success, error
  const [errorMessage, setErrorMessage] = useState("");

  useEffect(() => {
    const token = searchParams.get("token");
    
    if (!token) {
      setStatus("error");
      setErrorMessage("Invalid magic link. Please request a new one.");
      return;
    }

    verifyToken(token);
  }, [searchParams]);

  const verifyToken = async (token) => {
    try {
      const response = await apiClient.post("/auth/magic-link/verify", { token });
      
      if (response.data.success) {
        // Store session token in localStorage so PWA context can pick it up
        if (response.data.session_token) {
          storeSessionToken(response.data.session_token);
        }
        if (response.data.user) setUser(response.data.user);
        setStatus("success");
        
        // Save browser timezone
        try {
          const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
          apiClient.put("/user/timezone", { timezone: tz });
        } catch {}
        
        const importedCount = response.data.imported_gigs_count || 0;
        toast.success(
          importedCount > 0
            ? `Welcome to Fretless! We've added ${importedCount} confirmed ${importedCount === 1 ? 'gig' : 'gigs'} to your account.`
            : "Welcome to Fretless!"
        );
        
        // Detect if we're in a standalone PWA or a browser tab
        const isPWA = window.matchMedia('(display-mode: standalone)').matches || window.navigator.standalone;
        
        if (isPWA) {
          // Already in PWA — navigate directly
          setTimeout(() => navigate('/dashboard', { replace: true }), 1500);
        } else {
          // Opened in browser tab from email — try to redirect to PWA
          // The session is stored in localStorage which is shared with PWA
          setTimeout(() => navigate('/dashboard', { replace: true }), 1500);
        }
      }
    } catch (error) {
      setStatus("error");
      setErrorMessage(error.response?.data?.detail || "Failed to verify magic link. Please request a new one.");
    }
  };

  return (
    <div className="min-h-screen bg-background noise-bg flex items-center justify-center p-4">
      <Card className="w-full max-w-md bg-card border-border">
        <CardContent className="pt-8 pb-8 text-center">
          <div className="flex justify-center mb-6">
            <img src="/icons/quaver.png" alt="Fretless" className="h-12 w-auto" />
          </div>

          {status === "verifying" && (
            <>
              <Loader2 className="h-16 w-16 text-primary mx-auto mb-4 animate-spin" />
              <h2 className="font-heading text-xl font-semibold mb-2">Verifying your magic link...</h2>
              <p className="text-muted-foreground">Please wait a moment</p>
            </>
          )}

          {status === "success" && (
            <>
              <CheckCircle2 className="h-16 w-16 text-emerald-500 mx-auto mb-4" />
              <h2 className="font-heading text-xl font-semibold mb-2">You're signed in!</h2>
              <p className="text-muted-foreground mb-4">Redirecting to your dashboard...</p>
              <p className="text-xs text-muted-foreground">
                If using the Fretless app, you can close this tab and open the app.
              </p>
            </>
          )}

          {status === "error" && (
            <>
              <XCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
              <h2 className="font-heading text-xl font-semibold mb-2">Unable to sign in</h2>
              <p className="text-muted-foreground mb-6">{errorMessage}</p>
              <Button 
                onClick={() => navigate("/")}
                className="bg-primary text-primary-foreground"
              >
                Back to Home
              </Button>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default MagicLinkVerify;
